/**
 * BulkCopyModal — Modal for copying multiple recordings as combined markdown
 *
 * Features:
 * - Options for what to include (transcripts, action items only)
 * - Preview of combined markdown
 * - Copy to clipboard
 */

import React, { useState, useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { X, Copy, Check, FileText, Loader2 } from 'lucide-react';
import apiClient from '../../lib/apiClient';

function fmtTimestamp(ms) {
  if (!ms && ms !== 0) return '';
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function fmtDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// Parse action items from note content
function parseActionItems(noteRaw) {
  if (!noteRaw) return [];

  const items = [];
  let noteContent = null;

  if (typeof noteRaw === 'string') {
    try {
      const parsed = JSON.parse(noteRaw);
      noteContent = parsed;
    } catch {
      noteContent = noteRaw;
    }
  } else if (Array.isArray(noteRaw)) {
    const autoSum = noteRaw.find(n => n.data_type === 'auto_sum_note');
    if (autoSum?.data_content) {
      noteContent = autoSum.data_content;
    }
  } else {
    noteContent = noteRaw.data_content || noteRaw.content || noteRaw;
  }

  if (typeof noteContent === 'string') {
    const actionRegex = /^[\s]*(?:[-*]|\d+\.)\s*(?:\[[ x]\])?\s*(.+)$/gm;
    let match;
    while ((match = actionRegex.exec(noteContent)) !== null) {
      const item = match[1].trim();
      if (item.toLowerCase().includes('action') ||
          item.toLowerCase().includes('follow') ||
          item.toLowerCase().includes('next step') ||
          item.toLowerCase().includes('todo') ||
          item.toLowerCase().includes('need to') ||
          item.toLowerCase().includes('will ')) {
        items.push(item);
      }
    }
  }

  return items;
}

function BulkCopyModal({ recordings, onClose }) {
  const [includeTranscripts, setIncludeTranscripts] = useState(true);
  const [actionItemsOnly, setActionItemsOnly] = useState(false);
  const [copied, setCopied] = useState(false);

  // Fetch full details for all selected recordings
  const detailQueries = useQueries({
    queries: recordings.map(r => ({
      queryKey: ['plaud-recording', r.id],
      queryFn: () => apiClient.get(`/api/plaud/recordings/${r.id}`),
      staleTime: 60_000,
    })),
  });

  // Fetch transcripts if needed
  const transcriptQueries = useQueries({
    queries: includeTranscripts
      ? recordings.map(r => ({
          queryKey: ['plaud-transcript', r.id],
          queryFn: () => apiClient.get(`/api/plaud/recordings/${r.id}/transcript`),
          staleTime: 60_000,
        }))
      : [],
  });

  const isLoading = detailQueries.some(q => q.isLoading) ||
    (includeTranscripts && transcriptQueries.some(q => q.isLoading));

  // Generate combined markdown
  const markdown = useMemo(() => {
    if (isLoading) return 'Loading...';

    const lines = [];
    lines.push('# Meeting Recordings Export');
    lines.push('');
    lines.push(`**Exported:** ${new Date().toLocaleDateString()}`);
    lines.push(`**Recordings:** ${recordings.length}`);
    lines.push('');
    lines.push('---');
    lines.push('');

    // If action items only mode, aggregate all action items
    if (actionItemsOnly) {
      lines.push('## All Action Items');
      lines.push('');

      recordings.forEach((recording, i) => {
        const fullData = detailQueries[i]?.data;
        const actionItems = parseActionItems(fullData?.note_raw);

        if (actionItems.length > 0) {
          lines.push(`### ${recording.name}`);
          lines.push(`*${fmtDate(recording.recorded_at)}*`);
          lines.push('');
          actionItems.forEach(item => {
            lines.push(`- [ ] ${item}`);
          });
          lines.push('');
        }
      });

      return lines.join('\n');
    }

    // Full export
    recordings.forEach((recording, i) => {
      const fullData = detailQueries[i]?.data;
      const segments = includeTranscripts ? (transcriptQueries[i]?.data?.segments || []) : [];
      const actionItems = parseActionItems(fullData?.note_raw);

      lines.push(`## ${recording.name}`);
      lines.push('');
      lines.push(`**Date:** ${fmtDate(recording.recorded_at) || 'Unknown'}`);
      if (recording.duration_formatted) {
        lines.push(`**Duration:** ${recording.duration_formatted}`);
      }
      // categoryInfo.category is null when the backend classifier had nothing
      // to go on — don't print "**Category:** null" into the export.
      if (recording.categoryInfo?.category) {
        lines.push(`**Category:** ${recording.categoryInfo.category}`);
      }
      lines.push('');

      // Summary
      if (recording.summary) {
        lines.push('### Summary');
        lines.push('');
        lines.push(recording.summary);
        lines.push('');
      }

      // Action Items
      if (actionItems.length > 0) {
        lines.push('### Action Items');
        lines.push('');
        actionItems.forEach(item => {
          lines.push(`- [ ] ${item}`);
        });
        lines.push('');
      }

      // Transcript
      if (includeTranscripts && segments.length > 0) {
        lines.push('### Transcript');
        lines.push('');
        segments.forEach(seg => {
          const speaker = seg.speaker || 'Speaker';
          lines.push(`**[${fmtTimestamp(seg.start_ms)}] ${speaker}:**`);
          lines.push(seg.content);
          lines.push('');
        });
      }

      lines.push('---');
      lines.push('');
    });

    return lines.join('\n');
  }, [recordings, detailQueries, transcriptQueries, includeTranscripts, actionItemsOnly, isLoading]);

  // Convert markdown to simple HTML for Google Docs
  const markdownToHtml = (md) => {
    return md
      .replace(/^# (.+)$/gm, '<h1>$1</h1>')
      .replace(/^## (.+)$/gm, '<h2>$1</h2>')
      .replace(/^### (.+)$/gm, '<h3>$1</h3>')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/^- \[ \] (.+)$/gm, '<p>☐ $1</p>')
      .replace(/^- (.+)$/gm, '<p>• $1</p>')
      .replace(/^---$/gm, '<hr>')
      .replace(/\n\n/g, '</p><p>')
      .replace(/\n/g, '<br>');
  };

  const handleCopy = async () => {
    try {
      // Copy as HTML for Google Docs formatting
      const html = markdownToHtml(markdown);
      const htmlBlob = new Blob([html], { type: 'text/html' });
      const textBlob = new Blob([markdown], { type: 'text/plain' });

      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': htmlBlob,
          'text/plain': textBlob,
        }),
      ]);

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error('Failed to copy:', e);
    }
  };

  return (
    <div className="rp-modal-overlay" onClick={onClose}>
      <div className="rp-modal" onClick={e => e.stopPropagation()}>
        <div className="rp-modal-header">
          <h2><FileText size={18} /> Export {recordings.length} Recording{recordings.length !== 1 ? 's' : ''}</h2>
          <button className="rp-modal-close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="rp-modal-body">
          <div className="rp-modal-options">
            <label className="rp-modal-option">
              <input
                type="checkbox"
                checked={includeTranscripts}
                onChange={(e) => setIncludeTranscripts(e.target.checked)}
                disabled={actionItemsOnly}
              />
              Include full transcripts
            </label>
            <label className="rp-modal-option">
              <input
                type="checkbox"
                checked={actionItemsOnly}
                onChange={(e) => setActionItemsOnly(e.target.checked)}
              />
              Action items only
            </label>
          </div>

          <div className="rp-modal-preview">
            {isLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Loader2 size={16} className="rp-spin" />
                Loading recordings...
              </div>
            ) : (
              markdown.slice(0, 2000) + (markdown.length > 2000 ? '\n\n... (truncated preview)' : '')
            )}
          </div>
        </div>

        <div className="rp-modal-footer">
          <button className="rp-modal-btn secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className={`rp-modal-btn primary ${copied ? 'copied' : ''}`}
            onClick={handleCopy}
            disabled={isLoading}
          >
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? 'Copied!' : 'Copy to Clipboard'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default BulkCopyModal;
